/**
 * 화면 상세 — 편성 배정과 **왜 이게 나오는지**를 함께 본다.
 *
 * `trace` 를 그대로 보여주는 것이 이 화면의 핵심이다. 현장 지원에서 가장 자주 나오는
 * 질문이 "이 화면은 왜 이걸 틀고 있나요"이고, 지금까지는 로그를 뒤져야 답할 수 있었다.
 *
 * 모달이 아니라 **주소가 있는 화면**이다. 지원 요청에 링크를 그대로 붙여 넣을 수 있고,
 * 새로고침해도 보던 자리로 돌아온다.
 *
 * "지금 화면"의 그림은 **들어온 순간 한 번 찍은 것**이다. 화면을 따라 계속 바꾸지
 * 않는다. 대신 찍은 시각을 그림 옆에 밝혀, 방금 것인지 아까 것인지 헷갈리지 않게
 * 한다. 온라인 여부 같은 상태 표시만 스스로 갱신한다.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, History, Radio, RefreshCw, Send, Settings, Trash2 } from 'lucide-react';
import { headerPlace, zonedDayStart, zonedFieldsAt, zonedYmd } from '@signageone/core';
import type {
  Asset,
  CachedFile,
  Display,
  DisplayCache,
  DisplayStorage,
  Group,
  Me,
  Schedule,
  ScreenReport,
  SignageOneClient,
} from '../api/client.js';
import { behindReason } from '../agent-status.js';
import { useBrand } from '../components/brand.js';
import {
  Badge,
  Empty,
  ErrorNote,
  formatBytes,
  Loading,
  Modal,
  Panel,
  relativeTime,
  RelTime,
  StatusDot,
} from '../components/ui.js';
import { assignmentKeyFromPick, PlaybackPicker, pickFromAssignmentKey } from '../components/PlaybackPicker.js';
import { DirectBanner, DirectSendModal } from '../components/DirectSendModal.js';
import { Disclosure } from '../components/Disclosure.js';
import { EditableField } from '../components/EditableField.js';
import { LocationPicker } from '../components/LocationPicker.js';
import { addTags, sameTags, TagEditor, TagList } from '../components/TagEditor.js';
import { SchedulePreview } from '../components/SchedulePreview.js';
import { ScreenViewer } from '../components/ScreenViewer.js';
import { CAPTURE_WAIT_MS, takeCapture } from '../components/screenCapture.js';
import { DisplaySettings, settingsSectionsFor } from '../components/DisplaySettings.js';
import { HelpTip } from '../components/HelpTip.js';
import { StorageWarning, useStorageCheck } from '../components/StorageWarning.js';
import { assignmentCheckInput } from '../components/storageCheck.js';
import { assignmentOverride } from '../assignmentOverride.js';
import { useAction, useAsync, usePolling } from '../hooks.js';
import { can } from '../capabilities.js';
import { traceInPriorityOrder, traceStageLabel } from '../trace.js';
import { ACTION_LABEL, formatActor } from './Activities.js';
import { describeAudit, type NameLookup } from '../audit.js';
import { releaseVersions } from '../releases.js';
import { useTitleDetail } from '../components/pageTitle.js';
import { confirmDialog } from '../components/ConfirmDialog.js';
import { commandBlock, COMMANDS } from '../components/AgentControlForm.js';
import { DeviceInfo, deviceInfoSummary } from '../components/DeviceHealthPanel.js';
import { MoreMenu } from '../components/MoreMenu.js';
import { josa } from '../josa.js';
import { displayLive } from '../components/displayLive.js';

/**
 * 다시 물어보는 주기 — 상태 표시와, 캡처를 기다리는 동안의 편성 조회.
 *
 * 화면은 누가 보고 있으면 5초마다 자기 화면을 보고한다. 기다리는 동안 이보다
 * 뜸하게 물으면 이미 도착한 그림을 늦게 집어 온다.
 */
const LIVE_REFRESH_MS = 5_000;

/**
 * 낡은 캐시 보고를 받았을 때 새 보고를 기다리며 다시 묻는 간격과 그 상한.
 *
 * 캐시 목록은 평소 다시 묻지 않는다(조회가 곧 "보고 있다" 는 표식이라 계속 물으면 화면이 계속 훑는다). 다만 열었을
 * 때 보고가 낡았으면, 서버가 그 화면을 깨워 새 목록을 받게 하므로 **그것이 올 때까지만** 짧게 다시 묻는다. 화면이
 * 대답하지 않아도(다른 서버 벌에서 기다리는 화면은 다음 폴링 30초) 1분이면 그친다.
 */
const CACHE_CHASE_MS = 60_000;
const CACHE_CHASE_POLL_MS = 3_000;

/**
 * 집어 둔 그림이 서버에서 이미 지워졌을 때 다시 집는 횟수.
 *
 * 화면은 새 그림을 올릴 때마다 직전 것을 지우므로, 집은 순간과 받는 순간 사이에 한 번쯤
 * 어긋날 수 있다. 저장소가 계속 못 내주는데 끝없이 다시 찍으면 그동안 테넌트의 화면
 * 전부가 계속 찍는다.
 */
const MISSING_RETRY_LIMIT = 3;

/**
 * 이 화면에 실제로 걸린 스케줄.
 *
 * **화면 자체 배정이 먼저, 없으면 그룹의 것을 물려받는다**(resolve 2단계와 같은 규칙).
 * 그룹을 빼놓으면 그룹으로만 스케줄을 쓰는 화면이 "배정 없음"으로 보이고, 그러면
 * 왜 그 편성이 나오는지 화면 어디에서도 설명되지 않는다.
 */
export function effectiveScheduleOf(
  display: Pick<Display, 'assignment' | 'group_id'>,
  schedules: readonly Schedule[],
  groups: readonly Group[],
): { schedule: Schedule; inherited: boolean } | null {
  const find = (id: string | null): Schedule | null =>
    (id ? schedules.find((s) => s.id === id) : null) ?? null;

  if (display.assignment.kind === 'schedule') {
    const own = find(display.assignment.schedule_id);
    return own ? { schedule: own, inherited: false } : null;
  }
  // 자체 배정이 있으면(플레이리스트 등) 그룹은 보지 않는다 — 물려받는 조건이 아니다.
  if (display.assignment.kind !== 'none') return null;

  const group = display.group_id ? groups.find((g) => g.id === display.group_id) : null;
  if (!group || group.assignment.kind !== 'schedule') return null;
  const inheritedSchedule = find(group.assignment.schedule_id);
  return inheritedSchedule ? { schedule: inheritedSchedule, inherited: true } : null;
}

/**
 * 위치 입력칸에 보일 값.
 *
 * **폴링과 부딪히는 자리다.** 이 화면은 5초마다 스스로 다시 불러오는데, 서버 값을
 * 그대로 입력칸에 물리면 **타이핑하는 도중에 그 글자가 덮어써진다.** 고치기 시작한
 * 순간부터는 초안이 먼저고, 저장을 마쳐 초안을 비운 뒤에야 서버 값을 다시 따른다.
 */
export function locationFieldValue(draft: string | null, saved: string): string {
  return draft ?? saved;
}

/**
 * 저장할 것이 있는가.
 *
 * 앞뒤 공백만 다른 것은 바뀐 것으로 치지 않는다 — 서버가 어차피 다듬어 저장하므로,
 * 공백 하나 때문에 [저장]이 떠 있으면 눌러도 아무 일이 없는 버튼이 된다.
 */
export function locationDirty(draft: string | null, saved: string): boolean {
  return draft !== null && draft.trim() !== saved.trim();
}

/**
 * 이 파일이 언제 나가는지 한 줄로.
 *
 * **지금 나가는 것과 앞으로 나갈 것을 섞지 않는다.** 섞으면 지금 재생 중인 파일 옆에
 * 이미 지난 시각이 "다음 출력"이라고 적힌다 — 서버가 `in_playback` 과 `next_play_at`
 * 을 따로 주는 이유가 그것이고, 여기서도 그대로 갈라 읽는다.
 */
export function playScheduleOf(
  file: Pick<CachedFile, 'in_playback' | 'next_play_at' | 'brand'>,
  now: Date = new Date(),
): { label: string; tone: 'now' | 'soon' | 'none' } {
  if (file.in_playback) return { label: '지금 편성', tone: 'now' };
  // 로고·사내 글꼴은 편성에 걸리지 않는다 — 템플릿이 나갈 때 함께 쓴다.
  if (file.brand) return { label: '템플릿과 함께', tone: 'soon' };
  if (file.next_play_at) return { label: relativeTime(file.next_play_at, now), tone: 'soon' };
  return { label: '예정 없음', tone: 'none' };
}

/**
 * 고른 파일을 지우면 무슨 일이 생기는가.
 *
 * `refetching` 은 **지금 편성에 쓰이고 있어 화면이 곧바로 다시 받을** 개수다. 막지
 * 않고 세어서 미리 말하는 이유는, 그것이 사고가 아니라 이 기능의 쓸모 중 하나이기
 * 때문이다 — 받다 만 파일이 깨져서 안 나올 때 지우고 다시 받게 하는 것이 현장에서
 * 이 버튼을 누르는 가장 흔한 이유다.
 */
export function evictSummary(files: readonly CachedFile[]): {
  count: number;
  bytes: number;
  refetching: number;
} {
  return {
    count: files.length,
    bytes: files.reduce((sum, file) => sum + file.bytes, 0),
    refetching: files.filter((file) => file.in_playback).length,
  };
}

/**
 * 목록이 지금 어떤 상태인지 한 줄.
 *
 * **빈 목록의 뜻이 두 가지라 반드시 갈라 말해야 한다.** 화면이 아직 보고하지 않은
 * 것과 정말로 아무것도 안 들고 있는 것은 완전히 다른 상황인데, 둘 다 빈 표로 보인다.
 * 처음 열었을 때 비어 있는 것은 정상이다 — 이 조회가 화면을 깨우고, 다음 폴링에 찬다.
 */
export function cacheStatusOf(
  cache: DisplayCache | null,
  online: boolean,
  now: Date = new Date(),
): string {
  if (!cache) return '';
  /*
   * **에이전트가 이 기능을 모르는 경우를 가장 먼저 가른다.**
   *
   * 이걸 말하지 않으면 "30초쯤 뒤에 새로고침하세요" 를 믿고 기다렸다가, 영영 차지
   * 않는 목록을 보고 고장이라고 판단한다. 실제로 그렇게 됐다 — 옛 에이전트는 목록을
   * 올리라는 지시 자체를 모르고 조용히 무시한다. 조용한 무시는 고장과 구분되지 않는다.
   */
  if (!cache.agent_supported) {
    return cache.agent_version
      ? `목록 보고 미지원 · 에이전트 ${cache.agent_version}(${cache.min_agent_version} 이상 필요)`
      : '에이전트 버전을 보고받지 못했습니다';
  }
  if (cache.never_reported) {
    return online
      ? '목록 요청 중 · 30초 안에 표시됩니다'
      : '화면이 꺼져 있어 목록을 받지 못했습니다';
  }
  const when = relativeTime(cache.reported_at, now);
  if (!cache.stale) return `${when} 보고`;
  return online ? `${when} 보고 · 새 목록을 요청했습니다` : `${when} 보고 · 화면이 꺼져 있어 이후 달라졌을 수 있습니다`;
}

/**
 * 새 보고를 기다리며 목록을 다시 물을 때인가.
 *
 * 보고가 낡았거나 아직 없고, 화면이 켜져 있고, 에이전트가 목록 보고를 알 때만 — 그리고 연 지(또는 새로고침한 지)
 * 상한 안에서만. 꺼진 화면·옛 에이전트는 기다려도 오지 않는다.
 */
export function shouldChaseCache(
  cache: DisplayCache | null,
  online: boolean,
  nowMs: number,
  untilMs: number,
): boolean {
  if (!cache || !online || !cache.agent_supported) return false;
  if (!cache.stale && !cache.never_reported) return false;
  return nowMs < untilMs;
}

/**
 * 판정을 미룬 항목(`checking`)이 있을 때 목록 아래에 적을 한 줄. 없으면 null.
 *
 * "못 받음" 이 아니라는 것을 분명히 한다 — 낡은 보고와 지금 편성이 어긋난 것일 뿐, 그새 받았을 수 있다.
 */
export function cacheCheckingNote(cache: DisplayCache | null, online: boolean): string | null {
  const count = cache?.checking_count ?? 0;
  if (!cache || count === 0) return null;
  return online
    ? `마지막 보고에 없던 콘텐츠 ${count}개를 확인하고 있습니다 — 화면에 새 목록을 요청했습니다.`
    : `마지막 보고에 없던 콘텐츠 ${count}개는 화면이 꺼져 있어 받았는지 확인할 수 없습니다.`;
}

/**
 * 남은 공간을 처음 보고하는 에이전트 버전.
 *
 * **릴리스 번호가 달라지면 여기도 고쳐야 한다.** 이 문장을 읽는 사람은 그 값을 보고
 * 올릴지 말지 정하는데, 틀린 번호를 적어 두면 이미 올린 사람이 다시 올리려 하거나
 * 아직 낮은 사람이 기다리지 않는다. 0.6.6 다음 릴리스에 이 보고가 들어간다.
 */
export const MIN_STORAGE_AGENT_VERSION = '0.6.7';

/** 여유가 이보다 적으면 눈에 띄게 한다 — 영상 한 편이 수백 MB 라 1GB 아래는 곧 바닥이다. */
export const LOW_STORAGE_BYTES = 1024 ** 3;
/** 전체의 이 비율보다 적게 남으면 같이 경고한다. 작은 기기에서는 절대값만으로 늦다. */
export const LOW_STORAGE_RATIO = 0.1;

/**
 * 눈에 띄게 할 만큼 여유가 적은가 — 목록과 상세가 **같은 기준**으로 판단해야 한다.
 *
 * 모르는 것은 적은 것이 아니다(`null` → false). 보고하지 않는 기기를 경고로 칠하면
 * 목록이 노랗게 물들고, 그러면 정작 바닥난 기기가 묻힌다.
 */
export function lowStorage(storage: DisplayStorage | null | undefined): boolean {
  if (!storage) return false;
  if (storage.free_bytes < LOW_STORAGE_BYTES) return true;
  return storage.total_bytes > 0 && storage.free_bytes / storage.total_bytes < LOW_STORAGE_RATIO;
}

/**
 * 저장공간 한 줄 — "11.2 KB 사용 · 여유 24.6 GB".
 *
 * **쓴 양만으로는 현장의 질문에 답하지 못한다.** 운영이 묻는 것은 "이 영상을 더 보낼 수
 * 있나" 이고, 그 답은 남은 양에 있다. 공간이 바닥나면 웹뷰가 받아 둔 파일을 임의로 버려
 * 재생이 조용히 구멍 나므로, 바닥나기 전에 보여야 한다.
 *
 * **모른다와 0 을 가른다.** 옛 에이전트와 셸 없는 브라우저 키오스크는 여유를 보고하지
 * 않는다. 그때 0 으로 그리면 멀쩡한 화면이 "공간 없음" 으로 보이고, 그런 화면이 목록에
 * 섞이면 정작 진짜로 바닥난 화면을 못 찾는다.
 *
 * **수의 출처를 함께 적는다.** `disk` 는 파티션의 실제 여유지만 `quota` 는 브라우저가 이
 * 오리진에 허락한 양이라 디스크 여유와 다르고 상황에 따라 변한다. 어느 것인지 적지 않으면
 * 사람이 그 수를 어디까지 믿을지 알 수 없다.
 */
export function storageLine(
  cacheBytes: number | null,
  storage: DisplayStorage | null | undefined,
): { text: string; note: string; low: boolean } {
  const used = `${formatBytes(cacheBytes)} 사용`;
  if (!storage) {
    return {
      text: used,
      note: `남은 공간을 보고받지 못했습니다(에이전트 ${MIN_STORAGE_AGENT_VERSION} 이상 필요)`,
      low: false,
    };
  }

  return {
    text: `${used} · 여유 ${formatBytes(storage.free_bytes)}`,
    note:
      storage.source === 'disk'
        ? `기기 저장소 ${formatBytes(storage.total_bytes)} 중`
        : `웹뷰 할당량 ${formatBytes(storage.total_bytes)} 기준 · 실제 디스크와 다를 수 있습니다`,
    low: lowStorage(storage),
  };
}

/**
 * 크기 칸에 적을 한 줄.
 *
 * **빈 칸을 그대로 두면 안 된다.** 이 목록에는 편성에 걸린 것과 화면이 들고 있는 것이
 * 함께 실리므로, 크기가 비어 있는 이유가 셋이다 — 받을 파일이 애초에 없거나(템플릿·웹),
 * 받아야 하는데 못 받았거나, 아직 보고를 못 받아 모르거나. 셋을 같은 빈 칸으로 두면
 * 가장 중요한 "못 받음"이 눈에 띄지 않는다.
 *
 * 템플릿·웹은 예전에 "파일 없음" 이라 적었는데, 기기에 없다는 말로 읽혔다(2026-10-22 사용자 — "템플릿 파일을 기기에
 * 저장하고 출력하는거 아니야?"). 템플릿은 내용이 **편성에 실려 와 기기에 함께 저장되고**(오프라인·다시 켜도 그대로
 * 나간다) 웹은 외부 주소를 그때그때 연다 — 그대로 적는다.
 */
export function fileStateLabel(
  file: Pick<CachedFile, 'state' | 'bytes' | 'type'>,
): { text: string; tone: 'normal' | 'muted' | 'warn' } {
  switch (file.state) {
    case 'cached':
      // 크기를 적어 두기 전에 받은 파일은 0 으로 온다 — 지어내지 않는다.
      return file.bytes > 0
        ? { text: formatBytes(file.bytes), tone: 'normal' }
        : { text: '크기 모름', tone: 'muted' };
    case 'no_file':
      return file.type === 'web' ? { text: '외부 주소', tone: 'muted' } : { text: '편성에 포함', tone: 'muted' };
    case 'not_yet':
      // 지금 편성이 아니라 아직 안 받았을 뿐이다 — 경고가 아니다.
      return { text: '나갈 때 받음', tone: 'muted' };
    case 'missing_file':
      return { text: '못 받음', tone: 'warn' };
    case 'checking':
      // 낡은 보고로는 단정하지 않는다 — 새 보고가 오면 판정된다.
      return { text: '확인 중', tone: 'muted' };
    case 'unknown':
      return { text: '모름', tone: 'muted' };
  }
}

/** 받아 둔 파일의 종류 — 같은 제목의 영상·이미지를 가른다. */
const FILE_TYPE_LABEL: Record<Asset['type'], string> = {
  image: '이미지',
  video: '영상',
  web: '웹',
  template: '템플릿',
  html: 'HTML',
};

/**
 * 기기 목록을 셋으로 가른다(2026-10-20) — **받아 둔 파일**(표), **편성에 있지만 기기에 없는 것**(못 받음·나갈 때 받음·
 * 확인 중·모름), **파일 없이 나가는 것**(템플릿·웹). 표는 기기에 실제로 있는 파일만 싣는다 — 크기와 받은 때가 뜻이 있는
 * 것은 그것뿐이다. 없는 것은 표 아래에 따로 적어, 편성과 보유의 어긋남("못 받음")을 놓치지 않게 한다.
 */
export function splitDeviceFiles(items: readonly CachedFile[]): {
  downloaded: CachedFile[];
  pending: CachedFile[];
  fileless: CachedFile[];
} {
  return {
    downloaded: items.filter((file) => file.state === 'cached'),
    pending: items.filter((file) => file.state !== 'cached' && file.state !== 'no_file'),
    fileless: items.filter((file) => file.state === 'no_file'),
  };
}

/**
 * 표에 싣는 줄 — 받아 둔 파일, 그 아래 파일 없이 나가는 것(템플릿·웹)(2026-10-21).
 *
 * 2026-10-20 에는 표에 받아 둔 파일만 싣고 템플릿·웹은 표 아래 한 줄 글("파일 없이 나가는 콘텐츠 … 개 — 이름")로 뺐다.
 * 그랬더니 지금 나가고 있는 템플릿('일정/이벤트')이 목록에 없다고 읽혔다 — 그 한 줄은 눈에 띄지 않고, 패널을 접으면
 * 아예 보이지 않는다. 이 패널을 여는 사람의 물음은 "이 기기가 틀 것을 갖고 있나" 이고, 템플릿·웹은 편성과 함께 받아
 * 바로 튼다(받을 파일이 없다). 그래서 같은 표에 싣되 크기 칸에 "편성에 포함"(웹은 "외부 주소")이라 말하고, 지울 사본이
 * 없어 고를 칸은 흐리게 둔다 — 템플릿에 넣은 그림·영상·HTML 은 그 줄 아래에 묶이고 템플릿 줄로 함께 고른다(`nestDeviceRows`).
 * 편성에 있는데 기기에 없는 것(못 받음 등)은 그대로 표 아래에 따로 적는다.
 */
export function deviceTableRows(split: { downloaded: readonly CachedFile[]; fileless: readonly CachedFile[] }): CachedFile[] {
  return [...split.downloaded, ...split.fileless];
}

/** 접힌 머리에 적는 개수 — 받아 둔 파일과 크기, 파일 없이 나가는 것의 수. */
export function deviceFilesSummary(
  split: { downloaded: readonly CachedFile[]; fileless: readonly CachedFile[] },
  totalBytes: number | null,
): string {
  return [
    split.downloaded.length > 0 && totalBytes !== null ? `파일 ${split.downloaded.length}개 · ${formatBytes(totalBytes)}` : '',
    split.fileless.length > 0 ? `템플릿·웹 ${split.fileless.length}개` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/** 받아 둔 파일이 하나도 없을 때 그 까닭 — 목록을 보고하지 못하는 기기·아직 보고 전·정말 없음. */
export function downloadedEmptyNote(
  cache: Pick<DisplayCache, 'agent_supported' | 'agent_version' | 'min_agent_version' | 'never_reported'>,
): { title: string; hint: string } {
  if (!cache.agent_supported) {
    return {
      title: '받은 파일 목록을 보고하지 않는 기기입니다',
      hint: `에이전트 ${cache.min_agent_version} 이상이 보고합니다${cache.agent_version ? `(지금 ${cache.agent_version})` : ''}. 재생에는 영향이 없습니다.`,
    };
  }
  if (cache.never_reported) {
    return { title: '아직 받은 파일 목록을 보고받지 못했습니다', hint: '화면이 켜져 있으면 곧 올립니다.' };
  }
  return {
    title: '기기에 받아 둔 콘텐츠 파일이 없습니다',
    hint: '템플릿은 편성에 실려 와 기기에 함께 저장되고, 웹은 외부 주소를 그때그때 엽니다 — 따로 받을 파일이 없습니다.',
  };
}

/**
 * 지울 수 있는 항목인가.
 *
 * 들고 있는 것만 지울 수 있다. 템플릿·웹은 지울 사본이 없고, 못 받았거나 모르는 것도
 * 지울 대상이 아니다. 고를 수 있게 두면 "지웠는데 아무 일도 안 일어나는" 버튼이 된다.
 * 템플릿이 쓰는 로고·사내 글꼴도 고를 수 없다 — 서버가 받지 않는다(2026-10-22 "로고는 지워도 템플릿 로고가 빠지지 않게").
 */
/**
 * 꺼진 화면에서 막힌 제어 단추의 설명 — 바로 보내기·캐시 지우기.
 *
 * 편성 배정·PIN·채널처럼 **정해 두는 것**은 오프라인이어도 받는다(다시 붙을 때 받아 간다).
 * 화면이 **지금** 해야 하는 일만 막는다.
 */
export const OFFLINE_CONTROL_HINT = '화면이 오프라인이라 쓸 수 없습니다';

export function isEvictable(file: Pick<CachedFile, 'state' | 'brand'>): boolean {
  return file.state === 'cached' && !file.brand;
}

/**
 * 표의 줄을 품은 콘텐츠 아래로 묶는다(2026-11-04) — 반반 화면·구역 나누기 템플릿에 넣은 그림·영상·HTML, 올린 HTML 이 품은
 * 영상(`used_by`)은 그 줄 바로 아래에 들여 쓴다. 템플릿을 보여 주려고 받은 파일이 무엇인지 한눈에 보이고, 따로 떨어져 "편성에
 * 없는 파일" 처럼 읽히지 않는다. 품은 콘텐츠가 표에 없으면 제자리에 둔다. 둘 이상이 품으면 표에서 먼저 나오는 쪽 아래 — 한
 * 줄은 한 번만 나온다. `depth` 는 들여쓰기 단계.
 */
export function nestDeviceRows(rows: readonly CachedFile[]): Array<{ file: CachedFile; depth: number }> {
  const inTable = new Set(rows.map((file) => file.asset_id));
  const parentOf = (file: CachedFile): string | null =>
    (file.used_by ?? []).find((id) => id !== file.asset_id && inTable.has(id)) ?? null;
  const out: Array<{ file: CachedFile; depth: number }> = [];
  const placed = new Set<string>();
  const place = (file: CachedFile, depth: number): void => {
    if (placed.has(file.asset_id)) return;
    placed.add(file.asset_id);
    out.push({ file, depth });
    for (const child of rows) if (parentOf(child) === file.asset_id) place(child, depth + 1);
  };
  for (const file of rows) if (parentOf(file) === null) place(file, 0);
  // 서로를 품는 줄은 있을 수 없지만, 있어도 빠뜨리지 않는다.
  for (const file of rows) place(file, 0);
  return out;
}

/**
 * 한 줄을 고르면 함께 고르는 파일(2026-11-04) — 그 줄(지울 수 있으면)과 그 줄이 품은 파일 가운데 지울 수 있는 것. 템플릿 줄은
 * 자신이 편성에 실려 와 지울 것이 없으니 품은 파일만 — 템플릿을 보여 주려고 받은 파일을 템플릿 줄 하나로 지운다. 비었으면
 * 고를 것이 없는 줄이다.
 */
export function evictFamily(file: CachedFile, rows: readonly CachedFile[]): string[] {
  const ids = isEvictable(file) ? [file.asset_id] : [];
  for (const other of rows) {
    if (other.asset_id === file.asset_id || !isEvictable(other)) continue;
    if ((other.used_by ?? []).includes(file.asset_id)) ids.push(other.asset_id);
  }
  return ids;
}

/** 지울 파일이 없는 줄(템플릿·웹)의 흐린 체크칸에 다는 까닭. 그 밖의 줄은 null — 체크칸을 두지 않는다. */
export function noFileReason(file: Pick<CachedFile, 'state' | 'type'>): string | null {
  if (file.state !== 'no_file') return null;
  return file.type === 'web'
    ? '웹 페이지는 외부 주소를 그때그때 열어 받아 둔 파일이 없습니다'
    : '템플릿의 글·설정은 편성과 함께 기기에 저장되어 따로 지울 파일이 없습니다 — 이 화면에서 빼려면 편성에서 빼세요';
}

export function DisplayDetailScreen({
  client,
  me,
}: {
  client: SignageOneClient;
  me: Me;
}): ReactNode {
  const navigate = useNavigate();
  const { brand } = useBrand();
  const { id = '' } = useParams();
  const display = useAsync(() => client.getDisplay(id), [client, id]);
  /* 탭 제목에 화면 이름을 — 상세를 여러 탭으로 열어도 어느 것이 무엇인지 보이게. */
  useTitleDetail(`/displays/${id}`, display.data?.name || display.data?.location);
  const playlists = useAsync(() => client.allPlaylists(), [client]);
  // 스케줄 규칙이 콘텐츠를 직접 가리킬 수 있어, 하루 미리보기의 이름표에 필요하다.
  const assets = useAsync(() => client.allAssets({ archived: 'false' }), [client]);
  const schedules = useAsync(() => client.listSchedules(), [client]);
  /*
   * 그룹도 읽는다. 화면에 자체 배정이 없으면 그룹의 배정을 물려받으므로(resolve 2단계),
   * 그룹만 보고 스케줄을 쓰는 화면이 여기서 "배정 없음"으로 보이면 안 된다.
   */
  const groups = useAsync(() => client.listGroups(), [client]);
  /* 위치 드롭다운이 고를 값. 서버가 세어 준다 — 목록을 받아 추리면 50대에서 잘린다. */
  const locations = useAsync(() => client.listLocations(), [client]);
  /* 태그 드롭다운이 고를 값 — 위치와 같이 서버가 센다. */
  const tagNames = useAsync(() => client.listDisplayTags(), [client]);
  const audit = useAsync(
    () => client.auditLogs({ limit: '50', resource_type: 'display', resource_id: id }),
    [client, id],
  );
  /* 고정할 버전은 **등록된 산출물** 중에서 고르게 한다 — 없는 버전을 적으면 조용히 안 나간다. */
  const catalog = useAsync(() => client.agentCatalog(), [client]);
  /*
   * 이 화면이 들고 있는 콘텐츠 파일.
   *
   * **폴링을 붙이지 않는다.** 이 조회 자체가 "보고 있다"는 신호라(서버
   * `markCacheWatching`), 5초마다 물으면 이 탭이 열려 있는 내내 화면이 자기 캐시를
   * 계속 훑는다. 목록은 자주 바뀌는 값도 아니다 — 새로고침 버튼이 다시 집어 온다.
   */
  const cache = useAsync(() => client.displayCache(id), [client, id]);
  /** 새 캐시 보고를 기다리며 다시 물어도 되는 마감 시각 — 열 때와 [새로고침] 때 새로 잡는다. */
  const [cacheChaseUntil, setCacheChaseUntil] = useState(() => Date.now() + CACHE_CHASE_MS);
  const versions = useMemo(() => releaseVersions(catalog.data?.items ?? []), [catalog.data]);

  /*
   * **고른 시각은 문자열이 아니라 정확한 시각으로 쥔다.**
   *
   * `datetime-local` 이 주는 "2026-09-03T13:30" 에는 타임존이 없다. 그대로 보내면
   * 서버가 `new Date()` 로 파싱하면서 **서버의 로컬 시각**으로 읽는다 — 화면이 뉴욕에
   * 있으면 열세 시간 어긋난 답을 보게 된다. 화면의 기준 타임존으로 해석해 실제 시각을
   * 만들어 두고, 서버에는 ISO 로 보낸다. null 이면 "지금" 이다.
   */
  const [pickedAt, setPickedAt] = useState<Date | null>(null);
  /*
   * 끄는 동안의 시각. **조회를 일으키지 않는다.**
   *
   * 손가락이 지나가는 매 순간마다 편성을 물으면 한 번 끌 때 수십 번을 두드린다.
   * 끄는 동안에는 이 값이 화면의 시각 표시만 따라가고, 놓는 순간 `pickedAt` 이 되어
   * 그때 한 번 조회한다.
   */
  const [scrubMinutes, setScrubMinutes] = useState<number | null>(null);
  const [sending, setSending] = useState(false);
  const [showLog, setShowLog] = useState(false);
  /** 편성이 어디에서 결정됐는지 보는 창. */
  const [showWhy, setShowWhy] = useState(false);
  /** 열려 있는 설정 탭. null 이면 창이 닫혀 있다. */
  const [showSettings, setShowSettings] = useState(false);
  /**
   * 위치 입력의 초안. null 이면 고치는 중이 아니라는 뜻이고(연필을 누르기 전), 그때는 서버 값을 그대로 보여준다.
   *
   * 초안을 따로 쥐지 않으면 5초 폴링이 타이핑 도중에 입력칸을 덮어쓴다.
   */
  const [locationDraft, setLocationDraft] = useState<string | null>(null);
  /** 고치는 중인 화면 이름 — null 이면 서버 값을 따른다. 비운 채 저장하면 칸을 짚는다(`nameInvalid`). */
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [nameInvalid, setNameInvalid] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  /** 고치는 중인 태그 — null 이면 서버 값을 따른다. 적는 칸의 글자도 쥔다: Enter 없이 [저장] 해도 붙인다. */
  const [tagsDraft, setTagsDraft] = useState<string[] | null>(null);
  const [tagText, setTagText] = useState('');
  /** 지우려고 고른 캐시 파일. 목록을 다시 집어 올 때마다 비운다. */
  const [pickedFiles, setPickedFiles] = useState<ReadonlySet<string>>(new Set());
  const preview = useAsync(
    () => client.previewDisplay(id, pickedAt?.toISOString()),
    [client, id, pickedAt],
  );
  const action = useAction();

  /** 들어온 순간의 화면. 한 번 집으면 이 뒤의 보고로 바꾸지 않는다 — 다시 찍을 때만 비운다. */
  const [capture, setCapture] = useState<ScreenReport | null>(null);
  /** 다시 찍을 때, 이보다 새로 찍힌 보고만 받는다. */
  const [captureAfter, setCaptureAfter] = useState<string | null>(null);
  /** 기다리다 포기했는가. */
  const [captureGaveUp, setCaptureGaveUp] = useState(false);
  /** 몇 번째 찍기인가. 기다리는 중에 다시 찍으면 기다리는 한도를 새로 잰다. */
  const [captureAttempt, setCaptureAttempt] = useState(0);
  /** 그림이 서버에서 지워져 다시 집은 횟수. */
  const missingRetries = useRef(0);

  const live = pickedAt === null;
  /*
   * 캡처를 기다리는 중인가.
   *
   * 오프라인 화면은 기다리지 않는다 — 찍어 보낼 수 없고, 기다리는 동안 다른 화면들만
   * 계속 찍게 된다.
   */
  const capturing = live && capture === null && !captureGaveUp && (display.data?.online ?? false);

  /*
   * **편성은 캡처를 기다리는 동안에만 다시 묻는다.**
   *
   * 편성 조회가 곧 "보고 있다" 는 표식이라(서버 `markWatching`), 캡처를 받은 뒤에도
   * 계속 물으면 이 탭이 열려 있는 내내 테넌트의 화면 전부가 5초마다 자기 화면을
   * 찍는다 — 아무도 그 그림을 보지 않는데도. 상태(`display`)는 표식과 무관해 계속
   * 따라간다. 특정 시각을 골라 둔 동안에는 둘 다 멈춘다 — 같은 답이 올 뿐이다.
   */
  usePolling(display.reload, LIVE_REFRESH_MS, live);
  usePolling(preview.reload, LIVE_REFRESH_MS, capturing);
  // 연 순간의 캐시 보고가 낡았으면 새 보고가 올 때까지만 다시 묻는다(`CACHE_CHASE_MS`).
  usePolling(
    cache.reload,
    CACHE_CHASE_POLL_MS,
    shouldChaseCache(cache.data, display.data?.online ?? false, Date.now(), cacheChaseUntil),
  );

  /*
   * 도착한 조회 결과에서 캡처를 집는다.
   *
   * 불러오는 중에는 보지 않는다. 다른 화면으로 넘어온 직후에는 앞 화면의 결과가 아직
   * 남아 있어서, 그것을 집으면 앞 화면의 그림이 이 화면 이름을 달고 걸린다.
   */
  useEffect(() => {
    if (capture !== null || preview.loading || !preview.data) return;
    const taken = takeCapture(preview.data, captureAfter);
    if (taken) setCapture(taken);
  }, [preview.data, preview.loading, capture, captureAfter]);

  useEffect(() => {
    if (!capturing) return;
    const timer = setTimeout(() => setCaptureGaveUp(true), CAPTURE_WAIT_MS);
    return () => clearTimeout(timer);
  }, [capturing, captureAttempt]);

  // 다른 화면으로 넘어오면 처음부터 찍는다.
  useEffect(() => {
    missingRetries.current = 0;
    setCapture(null);
    setCaptureAfter(null);
    setCaptureGaveUp(false);
  }, [id]);

  /**
   * 집어 둔 그림이 서버에서 이미 지워졌다 — 화면이 그새 새 그림을 올렸다.
   *
   * 그보다 새로 찍힌 것을 다시 집는다. 한도를 넘으면 편성 기준 그림으로 물러난다.
   */
  const captureMissing = (report: ScreenReport): void => {
    if (capture?.taken_at !== report.taken_at) return;
    missingRetries.current += 1;
    setCaptureAfter(report.taken_at);
    setCapture(null);
    if (missingRetries.current > MISSING_RETRY_LIMIT) setCaptureGaveUp(true);
    else preview.reload();
  };

  /** 지금 화면을 다시 찍어 온다. */
  const recapture = (): void => {
    missingRetries.current = 0;
    setCaptureAfter((current) => capture?.taken_at ?? current);
    setCapture(null);
    setCaptureGaveUp(false);
    setCaptureAttempt((n) => n + 1);
    preview.reload();
  };

  const assignmentValue = useMemo(() => {
    const assignment = display.data?.assignment;
    if (!assignment || assignment.kind === 'none') return '';
    return assignment.kind === 'playlist'
      ? `playlist:${assignment.playlist_id}`
      : `schedule:${assignment.schedule_id}`;
  }, [display.data]);

  /*
   * 지금 배정된 편성이 이 화면의 저장 공간에 들어가는가. 배정 칸은 고르는 즉시 저장되므로 고른 **뒤에**
   * 말한다 — 막지 않고, 모르면(여유를 보고하지 않는 화면) 아무 말도 하지 않는다. 그룹에서 물려받는
   * 편성은 이 칸이 고른 것이 아니라 묻지 않는다.
   */
  const storageShortfalls = useStorageCheck(client, assignmentCheckInput([id], assignmentValue));

  /*
   * 배정이 지금 덮여 있는가(`assignmentOverride.ts`) — 배정 칸에서 말한다. 전체 대체 캠페인이 이기고 있으면 배정을 바꿔도
   * 기기 화면이 그대로라 "아무 동작을 안 한다" 로 보였다(2026-10-28 사용자 보고). 캠페인이면 이름을 찾아 함께 적는다.
   */
  const override = assignmentOverride(preview.data);
  const overrideCampaignId = override?.kind === 'campaign' ? override.campaignId : null;
  const overrideCampaigns = useAsync(
    () => (overrideCampaignId ? client.listCampaigns() : Promise.resolve(null)),
    [client, overrideCampaignId],
  );
  const overrideCampaignName =
    overrideCampaigns.data?.items.find((campaign) => campaign.id === overrideCampaignId)?.name ?? null;

  /*
   * **훅은 조기 반환보다 앞에 있어야 한다.**
   *
   * 아래에 `if (display.error) return …`·`if (!info) return …` 이 있다. 그 뒤에 훅을 두면
   * 불러오는 동안에는 호출되지 않다가 데이터가 도착한 렌더에서 처음 호출되어,
   * React 가 "Rendered more hooks than during the previous render" 로 화면 전체를 버린다.
   * 실제로 그렇게 만들어 화면이 하얗게 됐다.
   */
  const effectiveSchedule = useMemo(
    () =>
      display.data
        ? effectiveScheduleOf(display.data, schedules.data?.items ?? [], groups.data?.items ?? [])
        : null,
    [display.data, schedules.data, groups.data],
  );
  /*
   * 변경 이력에 쓸 이름표.
   *
   * 감사로그에 남는 것은 id 다. "편성: 68be…c1 → 68bf…04" 는 아무에게도 아무 말도
   * 하지 않는다 — 이미 읽어 둔 목록으로 이름을 붙여 준다. 목록에 없는 id(지워졌거나
   * 바로 보내기가 만든 숨은 플레이리스트)는 `describeAudit` 이 뒷자리로 돌린다.
   */
  const lookupName = useCallback<NameLookup>(
    (kind, refId) => {
      if (kind === 'playlist') return playlists.data?.items.find((item) => item.id === refId)?.name;
      if (kind === 'schedule') return schedules.data?.items.find((item) => item.id === refId)?.name;
      return groups.data?.items.find((item) => item.id === refId)?.name;
    },
    [playlists.data, schedules.data, groups.data],
  );

  /** 화면 타임존이 스케줄 타임존을 덮는다(R5). */
  const effectiveTimezone =
    display.data?.timezone ?? effectiveSchedule?.schedule.timezone ?? 'Asia/Seoul';

  /*
   * **고른 시각을 화면의 기준 타임존으로 해석한다.**
   *
   * 타임라인이 보여 주는 하루는 그 화면의 타임존 기준이다. 거기서 13:30 을 눌렀으면
   * 묻는 것은 "그 화면의 13:30" 이지 보는 사람 브라우저의 13:30 이 아니다. 둘이 다른
   * 지역이면 답이 통째로 달라진다 — 그래서 core 의 `zonedDayStart` 로 그 날 자정을
   * 잡고 분을 더해 실제 시각을 만든다.
   */
  const pickFromTimeline = useCallback(
    (ymd: string, minutes: number): void => {
      const dayStart = zonedDayStart(ymd, effectiveTimezone);
      if (!dayStart) return;
      setScrubMinutes(null);
      setPickedAt(new Date(dayStart.getTime() + minutes * 60_000));
    },
    [effectiveTimezone],
  );

  /** 끄는 중 — 표시만 바꾼다. */
  const scrub = useCallback((_ymd: string, minutes: number): void => setScrubMinutes(minutes), []);

  /** 지금 화면이 가리키는 분. 끄는 중이면 그 값이 먼저다. */
  const shownMinutes = useMemo(() => {
    if (scrubMinutes !== null) return scrubMinutes;
    return zonedFieldsAt(pickedAt ?? new Date(), effectiveTimezone).minutes;
  }, [scrubMinutes, pickedAt, effectiveTimezone]);

  /** 시각 입력에 보이는 값(HH:MM). 끄는 동안 손을 따라 바로 바뀐다. */
  const pickedLabel = useMemo(
    () =>
      `${String(Math.floor(shownMinutes / 60)).padStart(2, '0')}:${String(shownMinutes % 60).padStart(2, '0')}`,
    [shownMinutes],
  );

  /** 고른 시각이 속한 날. 타임라인이 보여 주는 날과 같아야 한다. */
  const previewDayLabel = useMemo(
    () => zonedYmd(pickedAt ?? new Date(), effectiveTimezone),
    [pickedAt, effectiveTimezone],
  );

  /** 시각 입력. 날짜는 지금 보고 있는 날을 그대로 쓴다 — 날은 타임라인이 정한다. */
  const pickTime = useCallback(
    (value: string): void => {
      const parts = value.split(':');
      const h = Number.parseInt(parts[0] ?? '', 10);
      const m = Number.parseInt(parts[1] ?? '', 10);
      // 입력을 지우면 빈 문자열이 온다 — 그때는 아무것도 고르지 않은 것으로 둔다.
      if (Number.isNaN(h) || Number.isNaN(m)) return;
      pickFromTimeline(previewDayLabel, h * 60 + m);
    },
    [pickFromTimeline, previewDayLabel],
  );

  const changeAssignment = (value: string): void => {
    const assignment: Display['assignment'] = value
      ? {
          kind: value.startsWith('playlist:') ? 'playlist' : 'schedule',
          playlist_id: value.startsWith('playlist:') ? value.slice('playlist:'.length) : null,
          schedule_id: value.startsWith('schedule:') ? value.slice('schedule:'.length) : null,
        }
      : { kind: 'none', playlist_id: null, schedule_id: null };

    void action.run(
      () => client.updateDisplay(id, { assignment }),
      () => {
        display.reload();
        preview.reload();
        audit.reload();
      },
    );
  };

  /**
   * 파일 한 줄을 고르거나 푼다.
   *
   * **줄과 체크칸이 같은 함수를 부른다.** 예전에는 줄에만 동작이 있고 체크칸은
   * `onChange` 가 빈 함수에 클릭 전파까지 막아, **체크칸을 누르면 아무 일도 일어나지
   * 않았다** — 사람이 제일 먼저 누르는 자리가 그것인데. 키보드로 Space 를 눌러도 같았다.
   */
  const togglePicked = (assetIds: readonly string[]): void => {
    setPickedFiles((current) => {
      const next = new Set(current);
      // 템플릿 줄은 품은 파일을 한꺼번에 — 다 골라져 있으면 모두 풀고, 하나라도 빠졌으면 모두 고른다.
      const all = assetIds.every((assetId) => next.has(assetId));
      for (const assetId of assetIds) {
        if (all) next.delete(assetId);
        else next.add(assetId);
      }
      return next;
    });
  };

  /**
   * 고른 파일을 지우라고 지시한다.
   *
   * **누르기 전에 무슨 일이 생기는지 다 말한다.** 이 저장소의 파괴적 동작이 지키는
   * 규칙이고, 여기서는 말할 것이 둘 더 있다 — 지금 쓰이는 파일은 곧바로 다시 받는다는
   * 것, 그리고 꺼져 있는 화면에서는 지금 일어나지 않는다는 것이다.
   */
  const evictPicked = async (): Promise<void> => {
    const chosen = (cache.data?.items ?? []).filter((file) => pickedFiles.has(file.asset_id));
    if (chosen.length === 0) return;

    const summary = evictSummary(chosen);
    const names = chosen.slice(0, 5).map((file) => `· ${file.title}`).join('\n');
    const more = chosen.length > 5 ? `\n외 ${chosen.length - 5}건` : '';
    const refetchNote =
      summary.refetching > 0
        ? `\n이 중 ${summary.refetching}개는 지금 편성 중이라 곧바로 다시 받습니다.`
        : '';
    const ok = await confirmDialog({
      title: `이 화면에서 파일 ${chosen.length}개를 지울까요?`,
      body:
        `${formatBytes(summary.bytes)} — 콘텐츠 원본은 남고 이 화면의 사본만 지워집니다.${refetchNote}` +
        `\n\n${names}${more}`,
      confirmLabel: '지우기',
      danger: true,
    });
    if (!ok) return;

    void action.run(
      () => client.evictDisplayCache(id, chosen.map((file) => file.asset_id)),
      () => {
        setPickedFiles(new Set());
        cache.reload();
        audit.reload();
      },
      `파일 ${chosen.length}개를 지우라고 화면에 보냈습니다`,
    );
  };

  /**
   * 이름을 저장한다(2026-10-20) — 예전에는 등록할 때 적은 이름을 고칠 길이 없었다. 빈 이름은 받지 않는다:
   * 목록·모니터링·알림이 이름으로 화면을 가리킨다. 끝나면 초안을 비워 서버 값을 다시 따르게 한다.
   */
  const saveName = (): void => {
    if (nameDraft === null) return;
    const next = nameDraft.trim();
    if (!next) {
      setNameInvalid(true);
      nameRef.current?.focus();
      return;
    }
    // 바뀐 것이 없으면 요청 없이 닫는다 — [저장] 은 늘 누를 수 있다(`EditableField`).
    if (next === display.data?.name) {
      setNameDraft(null);
      return;
    }
    void action.run(
      () => client.updateDisplay(id, { name: next }),
      () => {
        setNameDraft(null);
        setNameInvalid(false);
        display.reload();
        audit.reload();
      },
      `화면 이름을 "${next}"${josa(next, '으로/로')} 바꿨습니다`,
    );
  };

  /** 위치를 저장한다. 끝나면 초안을 비워 서버 값을 다시 따르게 한다. */
  const saveLocation = (): void => {
    if (locationDraft === null) return;
    if (!locationDirty(locationDraft, display.data?.location ?? '')) {
      setLocationDraft(null);
      return;
    }
    void action.run(
      () => client.updateDisplay(id, { location: locationDraft.trim() }),
      () => {
        setLocationDraft(null);
        display.reload();
        audit.reload();
        // 새로 적은 이름은 그 순간부터 드롭다운에 있어야 한다.
        locations.reload();
      },
    );
  };

  /** 태그를 저장한다 — 적는 칸에 남은 글자까지. 끝나면 초안을 비우고, 새로 적은 태그가 드롭다운에 생긴다. */
  const saveTags = (): void => {
    if (tagsDraft === null) return;
    const next = addTags(tagsDraft, tagText);
    if (sameTags(next, display.data?.tags ?? [])) {
      setTagsDraft(null);
      setTagText('');
      return;
    }
    void action.run(
      () => client.updateDisplay(id, { tags: next }),
      () => {
        setTagsDraft(null);
        setTagText('');
        display.reload();
        preview.reload();
        audit.reload();
        tagNames.reload();
      },
    );
  };

  if (display.error) {
    return (
      <div className="screen">
        <BackLink />
        <ErrorNote message={display.error} />
      </div>
    );
  }

  if (!display.data) {
    return (
      <div className="screen">
        <BackLink />
        <Loading />
      </div>
    );
  }

  const info = display.data;
  /** 캐시 파일을 지울 권한이 있는가. 서버가 최종 관문이고, 여기서는 버튼을 감출 뿐이다. */
  const mayEvict = me.capabilities.includes('fleet:write');
  /**
   * 지금 지울 수 있는가 — 권한이 있고 **화면이 켜져 있어야** 한다.
   *
   * 지우기는 화면이 직접 하는 일이라, 꺼진 화면에 걸어 두면 눌러 놓고 아무 일도 일어나지 않는다
   * (서버도 409 로 막는다). 바로 보내기와 같은 규칙이다 — 오프라인이면 고르는 칸부터 내지 않는다.
   */
  const canEvict = mayEvict && info.online;
  /** 이 화면을 고칠 수 있는가 — 편성 배정·위치·태그·그룹·바로 보내기·숨기기가 이 능력이다. */
  const canEdit = can(me, 'schedule:write');
  /** 화면을 지울 수 있는가. */
  const canDelete = can(me, 'fleet:write');
  /** 변경 이력을 볼 수 있는가. 없으면 단추 자체를 내지 않는다 — 열어 봐야 403 이다. */
  const canAudit = can(me, 'audit:read');
  /** 설정 창에서 열 수 있는 탭. 하나도 없으면 톱니바퀴 자체를 내지 않는다. */
  const settingsSections = settingsSectionsFor(me.capabilities);
  const cachedFiles = cache.data?.items ?? [];
  /** 기기에 받아 둔 파일(표)·편성에 있지만 없는 것·파일 없이 나가는 것(`splitDeviceFiles`). */
  const deviceFiles = splitDeviceFiles(cachedFiles);
  const deviceRows = deviceTableRows(deviceFiles);
  /** 지울 수 있는 것만 고를 수 있다 — 템플릿·못 받은 것에는 지울 사본이 없다. */
  const evictableFiles = cachedFiles.filter(isEvictable);
  /** 편성에 있는데 이 화면이 들고 있지 않은 수. 접힌 머리에서도 이것만은 보여야 한다. */
  const missingCount = cache.data?.missing_count ?? 0;
  /** 낡은 보고라 판정을 미룬 수 — "못 받음" 대신 "확인 중" 으로 보인다. */
  const checkingCount = cache.data?.checking_count ?? 0;
  const checkingNote = cacheCheckingNote(cache.data, info.online);
  const allPicked =
    evictableFiles.length > 0 && evictableFiles.every((file) => pickedFiles.has(file.asset_id));


  return (
    <div className="screen animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <BackLink />
        {/*
          **다루는 동작과 치우는 동작을 갈라 둔다.**

          바로 보내기·새로고침은 하루에도 여러 번 누르는 것이고, 숨기기·삭제는 화면을
          목록에서 치우는 일이다. 나란히 붙여 두면 자주 누르는 버튼 옆에 되돌릴 수 없는
          버튼이 오게 된다 — 구분선으로 떼어 놓는다.
        */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn"
            onClick={() => {
              display.reload();
              // 그림까지 새로 받는다. 편성만 새로 받고 그림은 아까 것이면 둘이 어긋난다.
              recapture();
              // 캐시 목록은 평소 스스로 갱신하지 않는다 — 새로고침이 다시 집어 온다. 보고가 낡았으면 새 보고를 다시 기다린다.
              cache.reload();
              setCacheChaseUntil(Date.now() + CACHE_CHASE_MS);
              setPickedFiles(new Set());
            }}
          >
            <RefreshCw className="h-3.5 w-3.5" strokeWidth={2} />
            새로고침
          </button>

          {canAudit ? (
            <button
              type="button"
              className="btn"
              onClick={() => {
                setShowLog(true);
                audit.reload();
              }}
            >
              <History className="h-3.5 w-3.5" strokeWidth={2} />
              변경 이력
            </button>
          ) : null}

          {/*
            설정 — 톱니바퀴 하나.

            글자를 붙이지 않는다. 이 줄에서 자주 누르는 것은 바로 보내기·새로고침이고,
            설정은 가끔 여는 문이다. 톱니바퀴는 설명 없이도 읽히는 몇 안 되는 그림이다.
          */}
          {settingsSections.length > 0 ? (
            <button
              type="button"
              className="btn btn-icon"
              title="화면 설정"
              aria-label="화면 설정"
              onClick={() => setShowSettings(true)}
            >
              <Settings className="h-4 w-4" strokeWidth={2} />
            </button>
          ) : null}

          {canEdit || canDelete ? <span className="action-sep" aria-hidden="true" /> : null}

          {canEdit ? (
          <button
            type="button"
            className="btn"
            disabled={action.pending}
            title={
              info.hidden
                ? '목록에 다시 표시합니다.'
                : '목록에서만 감춥니다. 재생과 증빙은 계속됩니다.'
            }
            onClick={() =>
              void action.run(
                () => client.updateDisplay(id, { hidden: !info.hidden }),
                () => {
                  display.reload();
                  audit.reload();
                },
              )
            }
          >
            {info.hidden ? <Eye className="h-3.5 w-3.5" strokeWidth={2} /> : <EyeOff className="h-3.5 w-3.5" strokeWidth={2} />}
            {info.hidden ? '숨김 해제' : '숨기기'}
          </button>
          ) : null}

          {/* 삭제는 [⋯] 뒤에 — 기기를 현장에서 다시 페어링해야 하는 일이라 늘 보이는 빨간 단추로 두지 않는다(점진적 공개). */}
          {canDelete ? (
          <MoreMenu
            size="normal"
            label="화면 작업 더 보기"
            items={[
              {
                label: '삭제',
                danger: true,
                disabled: action.pending,
                title: '되돌릴 수 없습니다. 목록에서만 치우려면 숨기기를 쓰세요.',
                onSelect: async () => {
                  const ok = await confirmDialog({
                    title: '화면을 삭제할까요?',
                    body: (
                      <>
                        <strong>{info.name || '(이름 없음)'}</strong> — 되돌릴 수 없으며, 기기를 현장에서 다시 페어링해야
                        합니다. 목록에서만 치우려면 숨기기를 쓰세요.
                      </>
                    ),
                    confirmLabel: '삭제',
                    danger: true,
                  });
                  if (!ok) return;
                  void action.run(
                    () => client.deleteDisplay(id),
                    () => navigate('/displays'),
                    `"${info.name || '(이름 없음)'}" 화면을 삭제했습니다`,
                  );
                },
              },
            ]}
          />
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-[20px] font-semibold tracking-tight text-slate-900">{info.name || '(이름 없음)'}</h1>
        <span className="flex items-center gap-1.5 text-[13.5px] text-slate-500">
          <StatusDot online={info.online} />
          {info.online ? '온라인' : '오프라인'} · <RelTime iso={info.status.last_heartbeat_at} />
        </span>
        {info.hidden ? <Badge tone="neutral">목록에서 숨김</Badge> : null}
        {/* 끊긴 화면은 오프라인 하나만 — 마지막 보고에 남은 오류·정비 모드는 지금 상태가 아니다(`displayLive`). */}
        {/*
          오류 글은 기본 정보에 줄로 두지 않는다(2026-10-21 사용자 지시) — 이 배지에 마우스를 올리면 보인다. 끊긴 화면의
          오류는 마지막 보고에 남은 값이라 배지째 내지 않는다(`displayLive`).
        */}
        {displayLive(info).error ? (
          <Badge tone="danger" title={displayLive(info).error ?? undefined}>
            오류 보고
          </Badge>
        ) : null}
        {displayLive(info).maintenance ? <Badge tone="warn">정비 모드</Badge> : null}
        {/* 운영 시간 밖이라 패널을 꺼 두었다 — 꺼진 TV 를 고장으로 읽지 않게(2026-10-19 G9). */}
        {info.status.panel === 'off' ? <Badge tone="neutral">패널 꺼짐 · 운영 시간 밖</Badge> : null}
      </div>

      {/*
        정비 모드 — 현장에서 열어 둔 채 떠나면 재생이 멈춘 채 남는다. 예전에는 콘솔 어디에도 그 사실이 보이지 않아
        "켜져 있는데 안 나오는 화면" 으로만 읽혔고, 원격으로 풀 수도 없었다. 0.7.17 에이전트부터는 여기서 푼다
        (`exit_maintenance` 명령). 그 전 판은 여전히 현장에서 닫아야 한다.
      */}
      {displayLive(info).maintenance
        ? (() => {
            /** 원격으로 풀 수 없는 까닭 — 0.7.17 전 에이전트·꺼진 화면. 풀 수 있으면 null. */
            const block = commandBlock(info, 'exit_maintenance');
            return (
              <div
                className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-[13px] text-amber-900"
                role="status"
              >
                <span className="min-w-0 flex-1">
                  <strong className="font-semibold">정비 모드가 열려 있어 재생이 멈춰 있습니다.</strong>{' '}
                  {block === null
                    ? '현장에서 작업 중이 아니면 여기서 해제할 수 있습니다.'
                    : '현장에서 기기 화면 오른쪽 위의 [×] 를 눌러 닫아 주세요.'}
                  {block !== null && info.online ? <span className="block text-amber-800/80">원격 해제: {block}</span> : null}
                </span>
                {canDelete && block === null ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={action.pending}
                    onClick={async () => {
                      const name = info.name || '(이름 없음)';
                      const ok = await confirmDialog({
                        title: `${COMMANDS.exit_maintenance.label} — ${name}`,
                        body: COMMANDS.exit_maintenance.confirm(name),
                        confirmLabel: COMMANDS.exit_maintenance.label,
                      });
                      if (!ok) return;
                      void action.run(
                        () => client.sendDisplayCommand(id, 'exit_maintenance'),
                        () => {
                          display.reload();
                          audit.reload();
                        },
                        '정비 모드 해제를 보냈습니다 — 화면이 곧 재생으로 돌아갑니다',
                      );
                    }}
                  >
                    {COMMANDS.exit_maintenance.label}
                  </button>
                ) : null}
              </div>
            );
          })()
        : null}

      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}

      {/* 지금 편성이 가려져 있다는 사실은 화면 맨 위에서 말해야 한다. */}
      {info.direct && !info.direct.expired ? (
        <DirectBanner
          note={info.direct.note}
          expiresAt={info.direct.expires_at}
          createdAt={info.direct.created_at}
          deliveredAt={info.direct.delivered_at}
          pending={action.pending}
          /* 걸려 있다는 사실은 누구나 보되, 푸는 것은 편성을 고칠 수 있는 사람만. */
          onClear={!canEdit ? undefined : () =>
            void action.run(
              () => client.clearDirect(id),
              () => {
                display.reload();
                preview.reload();
                audit.reload();
              },
            )
          }
        />
      ) : null}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        {/*
          **왼쪽은 읽는 것, 오른쪽은 다루는 것.**

          이 화면에 오는 사람이 묻는 것은 대개 "지금 뭐가 나가나 → 왜 그게 나가나 →
          다음엔 언제 바뀌나 → 누가 뭘 바꿨나" 순이다. 그 넷을 넓은 왼쪽에 순서대로 둔다.
          하루 막대와 감사로그는 특히 폭이 필요하다 — 좁은 열에서는 구간과 이메일이 잘린다.

          오른쪽에는 설정과 참조를 둔다. 자주 보지만 넓을 필요는 없는 것들이다.

          **왼쪽과 오른쪽을 따로 쌓지 않고, 한 격자에서 줄마다 짝을 지어 높이를 맞춘다.**
          1줄은 지금 화면 ↔ 기본 정보, 2줄은 다운로드된 콘텐츠 ↔ 편성 배정이다. 열을 따로
          쌓았을 때는 그림 카드가 편성에 따라 580~930px 을 오가는데 기본 정보는 400px
          안팎이라, 양쪽 카드의 끝선이 어디에서도 맞지 않아 어수선해 보였다. 짧은 쪽
          카드 아래가 비는 것은 알고 고른 값이다 — 끝선이 맞는 편이 더 정돈돼 보인다.

          자리는 칸·줄로 적어 둔다. 적힌 차례는 좁은 화면에서 한 줄로 설 때의 차례라
          (그림 → 다운로드된 콘텐츠 → 기본 정보 → 편성 배정), 읽는 것이 먼저 오게 그대로 둔다.
        */}
        {/*
          그림이 먼저다. 항목 목록과 판단 근거는 그 다음이다 — 장애 대응은
          "무엇이 나가고 있나" 를 눈으로 확인하는 데서 시작한다.
        */}
        {preview.data ? (
          <ScreenViewer
            className="xl:col-span-2 xl:col-start-1 xl:row-start-1"
            preview={preview.data}
            // 템플릿 머리글에 적힐 글 — 환경 설정의 방식대로(화면이 받는 `header_place` 와 같은 셈).
            displayName={headerPlace(brand.place, info)}
            online={info.online}
            capture={capture}
            capturing={capturing}
            onRecapture={recapture}
            onCaptureMissing={captureMissing}
            onExplain={() => setShowWhy(true)}
            live={live}
          >
            {/*
              **오늘.** 그림이 "지금", 한 바퀴가 "지금 도는 것", 이것이 "오늘 언제
              바뀌나" 다 — 같은 질문의 배율만 다르므로 한 카드에 이어 붙인다.

              배정 칸은 "어느 스케줄인가"만 말한다. 현장에서 나오는 질문은 늘
              "지금 왜 이게 나오나", "다음엔 언제 바뀌나" 이고, 그 답이 여기 있다.
            */}
            {effectiveSchedule ? (
              <>
                <p className="rotation-head">
                  오늘 · <strong>{effectiveSchedule.schedule.name}</strong>
                  {effectiveSchedule.inherited ? <Badge tone="info">그룹에서 상속</Badge> : null}
                  {/*
                    시각 기준은 **카드 머리 오른쪽**에 둔다.

                    화면 타임존이 스케줄 타임존을 덮는다(R5). 덮고 있다는 사실이 안 보이면
                    "왜 한 시간 어긋나지" 를 스케줄 쪽에서 찾게 된다. 예전에는 이 문장이
                    구간 목록 아래 한 줄로 있었는데, 목록을 접어 두면서 **접힌 손잡이 밑에
                    혼자 남아** 무엇에 대한 설명인지 알 수 없게 됐다. 하루를 그리는 이 카드의
                    기준이므로 카드 이름과 같은 줄에 둔다.
                  */}
                  <span
                    className="rotation-head-note"
                    title={
                      info.timezone
                        ? '화면 타임존이 스케줄 타임존보다 우선합니다.'
                        : '화면 타임존이 없어 스케줄 타임존을 따릅니다.'
                    }
                  >
                    시각 기준 <span className="mono">{effectiveTimezone}</span>
                    <span className="cell-muted">{info.timezone ? ' · 화면 설정' : ' · 스케줄 기준'}</span>
                  </span>
                </p>
                <SchedulePreview
                  rules={effectiveSchedule.schedule.rules}
                  defaultPlaylistId={effectiveSchedule.schedule.default_playlist_id}
                  defaultAssetId={effectiveSchedule.schedule.default_asset_id ?? null}
                  timezone={effectiveTimezone}
                  playlists={playlists.data?.items ?? []}
                  assets={assets.data?.items ?? []}
                  showNow
                  onPick={pickFromTimeline}
                  onScrub={scrub}
                  markMinutes={live && scrubMinutes === null ? null : shownMinutes}
                  /*
                    구간 목록은 접어 둔다. 여기서 먼저 묻는 것은 "지금 뭐가 나가나" 이고
                    막대가 그 답을 이미 그린다 — 목록은 "몇 시에 뭘로 바뀌나" 를 짚어 볼
                    때 편다. 구간이 많은 화면에서는 이 목록이 아래 카드를 다 밀어냈다.
                  */
                  foldList
                  headerRight={
                    /*
                      **고치는 손잡이는 고치는 대상 바로 위에 둔다.**

                      예전에는 이 줄이 타임라인보다 위, 한 바퀴 목록 아래에 따로 있어서
                      막대와 입력이 서로 무관해 보였다. 끌면 이 입력이 손을 따라 바뀌므로
                      둘이 같은 것을 가리킨다는 사실이 눈에 보여야 한다.
                    */
                    <span className="pick-row">
                      <input
                        className="input input-time"
                        type="time"
                        value={pickedLabel}
                        onChange={(event) => pickTime(event.target.value)}
                        aria-label="이 시각의 화면 보기"
                      />
                      {live ? null : (
                        <button type="button" className="btn btn-tiny" onClick={() => setPickedAt(null)}>
                          <Radio className="h-3.5 w-3.5" strokeWidth={2} />
                          지금으로
                        </button>
                      )}
                    </span>
                  }
                />
              </>
            ) : null}
          </ScreenViewer>
        ) : null}


        {/*
          이 화면에 **받아 둔 콘텐츠 파일**과 그 크기(2026-10-20 — 예전 이름은 "적용된 콘텐츠" 였고 편성에 걸린 것을
          모두 한 표에 실었다). 표는 기기에 실제로 있는 파일만 싣고, 편성에 있는데 없는 것은 표 아래에 따로 적는다.

          아래는 예전 설명 — 무엇을 보여 주려는 표인가는 그대로다.

          편성이 "무엇이 나가야 하나"라면 이 표는 거기에 "그래서 갖고 있나"를 겹쳐 놓은
          것이다. 둘의 어긋남이 이 표의 값어치다 — 받다 만 파일이 깨져 있거나, 웹뷰가
          저장 공간이 빠듯해 임의로 버렸거나, 편성에서 뺀 지 오래인 파일이 자리만 먹고
          있거나.

          **처음에는 화면이 보고한 파일만 실었는데 그것은 반쪽이었다.** 템플릿·웹은
          받을 바이트가 없어 목록에서 빠지고, 그러면 템플릿만 걸린 화면은 표가 텅 비어
          "이 화면에 무엇이 적용돼 있나"에 아무 답도 주지 못한다. 서버는 그 기기가 지금도
          파일을 들고 있는지는 몰라도 무엇이 적용돼 있는지는 아니까, 둘을 합쳐 싣고
          줄마다 `state` 로 갈라 말한다.
        */}
        {/*
          **접어 두되, 접힌 머리가 다 말하게 한다.**

          표는 여섯 열에 수십 줄이라 펼쳐 두면 이 카드 하나가 화면을 다 먹는다. 그런데
          여기서 급한 것은 표가 아니라 **어긋남**이다 — 접었다고 "못 받음"이 안 보이면
          접기가 곧 숨기기가 된다. 그래서 개수와 못 받은 수는 머리에 남기고 표만 접는다.
        */}
        <Panel
          /* 편성 배정이 없는 사람에게는 오른쪽 칸이 비므로 줄 전체를 쓴다. */
          className={`xl:col-start-1 xl:row-start-2 ${canEdit ? 'xl:col-span-2' : 'xl:col-span-3'}`}
          title="다운로드된 콘텐츠"
          subtitle={[
            cacheStatusOf(cache.data, info.online),
            cache.data ? deviceFilesSummary(deviceFiles, cache.data.total_bytes) : '',
          ]
            .filter(Boolean)
            .join(' · ')}
          collapsible
          actions={
            missingCount > 0 || checkingCount > 0 || (canEvict && pickedFiles.size > 0) ? (
              <>
                {missingCount > 0 ? <Badge tone="danger">못 받음 {missingCount}</Badge> : null}
                {checkingCount > 0 ? <Badge tone="neutral">확인 중 {checkingCount}</Badge> : null}
                {canEvict && pickedFiles.size > 0 ? (
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={action.pending}
                    onClick={evictPicked}
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                    {pickedFiles.size}개 지우기
                  </button>
                ) : null}
              </>
            ) : null
          }
        >
          <div className="p-4">
            {cache.loading && !cache.data ? <Loading /> : null}
            {/* 부차적인 패널이다 — 여기서 실패해도 화면 상세 전체를 붉게 만들지 않는다. */}
            {cache.error ? <p className="hint">{cache.error}</p> : null}

            {/*
              목록이 비었다는 것은 이제 **편성도 비었다**는 뜻이다.

              편성에 걸린 것은 화면이 보고하지 않아도 서버가 알기 때문에, 예전처럼
              "아직 목록을 못 받았다"가 빈 표로 나타나지 않는다 — 그 상태는 줄마다
              `unknown` 으로 드러난다.
            */}
            {cache.data && deviceRows.length === 0 && !cache.loading ? (
              <Empty {...downloadedEmptyNote(cache.data)} />
            ) : null}
          </div>

          {deviceRows.length > 0 ? (
            /* 좁은 화면에서는 가로로 넘칠 수 있다 — 표만 따로 흐르게 둔다. */
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    {canEvict ? (
                      <th className="col-check">
                        {/* 고를 줄이 없으면(템플릿·웹뿐) 전체 선택도 두지 않는다 — 눌러도 아무 일이 없다. */}
                        {evictableFiles.length > 0 ? (
                          <input
                            type="checkbox"
                            aria-label="전체 선택"
                            checked={allPicked}
                            onChange={() =>
                              setPickedFiles(
                                allPicked
                                  ? new Set()
                                  : new Set(evictableFiles.map((file) => file.asset_id)),
                              )
                            }
                          />
                        ) : null}
                      </th>
                    ) : null}
                    <th>콘텐츠</th>
                    <th className="col-num">크기</th>
                    <th>받은 때</th>
                    <th>출력 예정</th>
                    <th>마지막 출력</th>
                  </tr>
                </thead>
                <tbody>
                  {nestDeviceRows(deviceRows).map(({ file, depth }) => {
                    const schedule = playScheduleOf(file);
                    const state = fileStateLabel(file);
                    /* 이 줄을 고르면 함께 고를 파일 — 템플릿 줄은 품은 파일(`evictFamily`). */
                    const family = evictFamily(file, deviceRows);
                    const picked = family.length > 0 && family.every((assetId) => pickedFiles.has(assetId));
                    const noFile = noFileReason(file);
                    const parent =
                      depth > 0 ? deviceRows.find((row) => file.used_by?.includes(row.asset_id)) : undefined;
                    return (
                      <tr
                        key={file.asset_id}
                        className={file.evicting ? 'row-muted' : undefined}
                        onClick={canEvict && family.length > 0 ? () => togglePicked(family) : undefined}
                      >
                        {canEvict ? (
                          <td className="col-check">
                            {family.length > 0 ? (
                              <input
                                type="checkbox"
                                checked={picked}
                                aria-label={
                                  isEvictable(file) ? `${file.title} 선택` : `${file.title} 의 파일 ${family.length}개 선택`
                                }
                                /*
                                  체크칸이 스스로 고른다. 전파는 막는다 — 막지 않으면 줄까지
                                  같이 받아 두 번 뒤집히고, 결국 아무 일도 일어나지 않는다.
                                */
                                onChange={() => togglePicked(family)}
                                onClick={(event) => event.stopPropagation()}
                              />
                            ) : noFile ? (
                              /*
                                지울 파일이 없는 템플릿·웹 — 흐린 칸에 까닭을 단다(2026-11-04). 칸이 아예 없으면 "지울 수 없는
                                고장" 으로 읽혔다. 누를 수 없는 칸은 마우스 알림을 받지 못해 감싼 칸에 단다.
                              */
                              <span title={noFile} className="inline-flex">
                                <input
                                  type="checkbox"
                                  disabled
                                  className="cursor-not-allowed opacity-40"
                                  aria-label={`${file.title} — ${noFile}`}
                                />
                              </span>
                            ) : null}
                          </td>
                        ) : null}
                        <td className="cell-strong">
                          {depth > 0 ? (
                            <span
                              className="mr-1 text-slate-300"
                              style={{ marginLeft: (depth - 1) * 16 }}
                              title={parent ? `「${parent.title}」 에 넣은 파일` : undefined}
                              aria-hidden="true"
                            >
                              └
                            </span>
                          ) : null}
                          {file.title}
                          {file.type ? <span className="hint hint-inline">{FILE_TYPE_LABEL[file.type] ?? file.type}</span> : null}
                          {/* 템플릿이 쓰는 로고·사내 글꼴 — 편성에 없어도 쓰는 파일이라 지울 수 없다. */}
                          {file.brand ? (
                            <span title="환경 설정 ▸ 브랜드의 파일 — 템플릿이 늘 쓰므로 지울 수 없습니다">
                              <Badge tone="info">{file.brand === 'logo' ? '템플릿 로고' : '템플릿 글꼴'}</Badge>
                            </span>
                          ) : null}
                          {/* 편성에서 빠진 지 오래인 파일은 자리만 먹는다 — 지울지 고를 근거다. */}
                          {!file.in_playback && !file.brand ? <Badge tone="neutral">편성에 없음</Badge> : null}
                          {file.missing ? <Badge tone="warn">자산 삭제됨</Badge> : null}
                          {file.archived ? <Badge tone="neutral">보관됨</Badge> : null}
                          {file.evicting ? <Badge tone="info">지우는 중</Badge> : null}
                        </td>
                        {/* 크기를 적어 두기 전에 받은 파일은 0 으로 온다 — "크기 모름" 이라 적는다(`fileStateLabel`). */}
                        <td className={`col-num whitespace-nowrap ${state.tone === 'muted' ? 'cell-muted' : ''}`}>{state.text}</td>
                        <td className="cell-muted whitespace-nowrap">
                          {file.cached_at ? (
                            <span title={new Date(file.cached_at).toLocaleString('ko-KR')}>
                              <RelTime iso={file.cached_at} />
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className={schedule.tone === 'none' ? 'cell-muted' : undefined}>
                          {schedule.tone === 'now' ? (
                            <Badge tone="ok">지금 편성</Badge>
                          ) : (
                            <span
                              title={
                                file.next_play_at
                                  ? new Date(file.next_play_at).toLocaleString('ko-KR')
                                  : `${Math.round((cache.data?.lookahead_min ?? 0) / 60)}시간 안에 출력 예정 없음`
                              }
                            >
                              {schedule.label}
                            </span>
                          )}
                        </td>
                        <td className="cell-muted">
                          <span
                            title={
                              file.last_played_at
                                ? new Date(file.last_played_at).toLocaleString('ko-KR')
                                : undefined
                            }
                          >
                            {file.last_played_at ? <RelTime iso={file.last_played_at} /> : file.brand ? '—' : '기록 없음'}
                          </span>
                          {file.play_count > 0 ? (
                            <span className="hint">
                              {' '}
                              · {cache.data?.history_days ?? 30}일간 {file.play_count}회
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}

          {cache.data && cachedFiles.length > 0 ? (
            <div className="space-y-2 p-4">
              {/*
                편성에 있지만 기기에 없는 것 — 표에서는 빠졌지만 이 패널의 값어치(편성과 보유의 어긋남)는 여기 남는다.
                어느 콘텐츠인지 줄마다 적는다(몇 개인지만으로는 찾아갈 수 없다).
              */}
              {deviceFiles.pending.length > 0 ? (
                <div>
                  <p className="hint mb-1">편성에 있지만 기기에 아직 없는 콘텐츠</p>
                  <ul className="flex flex-wrap gap-1.5">
                    {deviceFiles.pending.map((file) => {
                      const state = fileStateLabel(file);
                      /* 템플릿·HTML 에 넣은 파일이면 어디에 넣은 것인지 — 제목만으로는 왜 받아야 하는지 모른다. */
                      const parent = cachedFiles.find((row) => file.used_by?.[0] === row.asset_id);
                      return (
                        <li key={file.asset_id} className="flex items-center gap-1 text-[13px]">
                          <span className={state.tone === 'warn' ? 'font-medium text-slate-800' : 'text-slate-600'}>{file.title}</span>
                          {parent ? <span className="hint">「{parent.title}」 안</span> : null}
                          <span
                            title={
                              state.tone === 'warn'
                                ? '편성에 있지만 화면에 없습니다. 받기에 실패했거나 캐시에서 지워졌습니다.'
                                : undefined
                            }
                          >
                            <Badge tone={state.tone === 'warn' ? 'danger' : 'neutral'}>{state.text}</Badge>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
              {cache.data.missing_count > 0 ? (
                <p className="hint cell-danger">
                  받지 못한 콘텐츠가 {cache.data.missing_count}개 있습니다. 화면이 이 콘텐츠를 건너뛸 수 있습니다.
                </p>
              ) : null}
              {checkingNote ? <p className="hint">{checkingNote}</p> : null}
              <p className="hint">
                받아 둔 파일 {deviceFiles.downloaded.length}개 · 기기 보관 합계 {formatBytes(cache.data.total_bytes)}
                {cache.data.truncated ? ' · 앞의 1,000개만 표시' : ''}
                {/* 지워진 콘텐츠의 파일은 "(삭제된 콘텐츠)" 줄로 표에 나온다 — 여기는 콘텐츠·사내 글꼴로 확인되지 않는 나머지다. */}
                {cache.data.unknown_count > 0 ? ` · 콘텐츠로 확인되지 않는 파일 ${cache.data.unknown_count}개` : ''}
                {canEvict ? (
                  <HelpTip
                    text="지우기는 이 화면의 사본만 지우며 콘텐츠 원본은 남습니다. 편성 중인 파일은 곧바로 다시 받으므로 깨진 파일을 새로 받게 할 때 씁니다."
                    label="캐시 지우기 도움말"
                  />
                ) : null}
              </p>
              {mayEvict && !info.online ? (
                <p className="hint">화면이 오프라인이라 지울 수 없습니다.</p>
              ) : null}
            </div>
          ) : null}
        </Panel>

        {/* ── 설정과 참조 ──────────────────────────────────────── */}
        <Panel title="기본 정보" className="xl:col-start-3 xl:row-start-1">
          <div className="p-4">
            <dl className="kv">
              {/*
                이름·위치·태그 — 평소에는 값만 보이고, 오른쪽 연필을 눌렀을 때만 고치는 칸과 [저장]·[취소] 가 나온다
                (`EditableField`, 2026-10-20 사용자 지시). 예전에는 늘 입력칸·드롭다운으로 펼쳐져 있어 값이 눈에 들어오지
                않았고, 드롭다운을 잘못 건드리면 그대로 저장됐다. 입력칸에서 Enter 는 저장, Esc 는 취소.

                이름 — 비운 채 저장하면 칸을 빨갛게 짚는다: 목록·모니터링·알림이 이름으로 화면을 가리킨다.
              */}
              <dt>이름</dt>
              <dd>
                <EditableField
                  label="이름"
                  view={info.name || '—'}
                  canEdit={canEdit}
                  editing={nameDraft !== null}
                  saving={action.pending}
                  error={nameInvalid ? '이름을 입력하세요' : null}
                  onEdit={() => {
                    setNameDraft(info.name);
                    setNameInvalid(false);
                  }}
                  onSave={saveName}
                  onCancel={() => {
                    setNameDraft(null);
                    setNameInvalid(false);
                  }}
                >
                  <input
                    ref={nameRef}
                    className="input"
                    value={nameDraft ?? ''}
                    maxLength={200}
                    placeholder="화면 이름"
                    aria-label="화면 이름"
                    aria-invalid={nameInvalid || undefined}
                    disabled={action.pending}
                    onChange={(event) => {
                      setNameDraft(event.target.value);
                      setNameInvalid(false);
                    }}
                  />
                </EditableField>
              </dd>
              {/*
                위치 — 모니터링 벽이 위치로 묶이면서 이 값이 곧 벽의 구획이 됐다. 비어 있거나 틀린 화면은 "위치 미지정"
                칸에 남아 아무 지점에도 속하지 않는다. 고르는 것은 등록된 위치 드롭다운, 새 이름은 [새 위치 직접 입력…].
              */}
              <dt>위치</dt>
              <dd>
                <EditableField
                  label="위치"
                  view={info.location || <span className="cell-muted">위치 미지정</span>}
                  canEdit={canEdit}
                  editing={locationDraft !== null}
                  saving={action.pending}
                  onEdit={() => setLocationDraft(info.location)}
                  onSave={saveLocation}
                  onCancel={() => setLocationDraft(null)}
                >
                  <LocationPicker
                    value={locationFieldValue(locationDraft, info.location)}
                    known={(locations.data?.items ?? []).map((item) => item.name)}
                    disabled={action.pending}
                    onChange={setLocationDraft}
                  />
                </EditableField>
              </dd>
              {/*
                등록할 때 API 로 들어오는 `region` 은 여기 두지 않는다. 콘솔이 묶고 거르고
                옮기는 축은 위의 **위치** 하나이고(목록 거르개·일괄 이동·모니터링 벽·대시보드),
                그 옆에 고칠 수 없는 둘째 축을 세워 두면 "둘 중 어느 것이 맞는 값인가" 를
                묻게 된다. 값은 API 와 등록 이력(감사로그)에 그대로 남는다.
              */}
              {/*
                태그와 그룹 — 캠페인 대상과 편성 상속이 이 둘로 정해진다. 예전에는 보기만 했고
                붙이는 길은 연동 API 뿐이라, 캠페인의 "태그로 지정" 을 콘솔만으로는 쓸 수 없었다.
                태그도 위치와 같이 쓰이는 태그 드롭다운에서 고르고, 새 태그는 [새 태그 직접 입력…].
              */}
              <dt>
                태그
                <HelpTip text="캠페인 대상을 태그로 지정할 때 씁니다. 쓰이고 있는 태그에서 고르거나 새 태그를 입력합니다." label="태그 도움말" />
              </dt>
              <dd>
                <EditableField
                  label="태그"
                  view={info.tags.length > 0 ? <TagList tags={info.tags} /> : <span className="cell-muted">태그 없음</span>}
                  canEdit={canEdit}
                  editing={tagsDraft !== null}
                  saving={action.pending}
                  onEdit={() => {
                    setTagsDraft(info.tags);
                    setTagText('');
                  }}
                  onSave={saveTags}
                  onCancel={() => {
                    setTagsDraft(null);
                    setTagText('');
                  }}
                >
                  <TagEditor
                    tags={tagsDraft ?? info.tags}
                    known={(tagNames.data?.items ?? []).map((item) => item.name)}
                    disabled={action.pending}
                    text={tagText}
                    onTextChange={setTagText}
                    onChange={setTagsDraft}
                  />
                </EditableField>
              </dd>
              <dt>
                그룹
                <HelpTip text="화면에 편성 배정이 없으면 그룹의 편성을 따릅니다." label="그룹 도움말" />
              </dt>
              <dd>
                {!canEdit ? (
                  <span>
                    {groups.data?.items.find((group) => group.id === info.group_id)?.name ?? '그룹 없음'}
                  </span>
                ) : (groups.data?.items.length ?? 0) > 0 || info.group_id ? (
                  <select
                    className="input"
                    value={info.group_id ?? ''}
                    disabled={action.pending}
                    aria-label="그룹"
                    onChange={(event) =>
                      void action.run(
                        () => client.updateDisplay(id, { group_id: event.target.value || null }),
                        () => {
                          display.reload();
                          preview.reload();
                          audit.reload();
                        },
                      )
                    }
                  >
                    <option value="">그룹 없음</option>
                    {(groups.data?.items ?? []).map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="cell-muted">그룹 없음</span>
                )}
              </dd>
              <dt>플레이어</dt>
              <dd>
                {info.status.app_version ?? '—'} · {info.channel}
                {info.pinned_version ? (
                  <Badge tone="warn">v{info.pinned_version} 고정</Badge>
                ) : null}
                {/*
                  받아야 할 버전보다 낮게 돌고 있다면 여기에 말한다.

                      승격은 화면이 물어봐야 일어난다 — 콘솔에서 올려도 물지 않는 화면은
                      그대로 멈춰 있고, 그 침묵은 지금까지 어느 화면에도 나타나지 않았다.
                */}
                {(() => {
                  const reason = behindReason(info.agent);
                  if (!reason) return null;
                  return (
                    <span title={reason.hint}>
                      <Badge tone={reason.tone}>{reason.label}</Badge>
                    </span>
                  );
                })()}
              </dd>
              <dt>저장 공간</dt>
              <dd>
                {(() => {
                  const line = storageLine(info.status.cache_bytes, info.status.storage);
                  return (
                    <span className="block">
                      <span className={line.low ? 'font-medium text-amber-700' : undefined}>
                        {line.text}
                      </span>
                      <span className="mt-0.5 block text-[11.5px] text-slate-400">{line.note}</span>
                    </span>
                  );
                })()}
              </dd>
            </dl>
            {/*
              기기를 고칠 때만 찾는 값은 [기기 정보] 뒤로(점진적 공개) — 운영에 쓰는 이름·위치·태그·그룹·버전·저장 공간이
              먼저 보인다(오류 글은 머리의 "오류 보고" 배지에 — 2026-10-21 기본 정보에서 뺐다). 기기 상태(CPU·메모리·온도·망)와 기기 로그도 여기 함께 둔다(2026-10-20 — 예전에는 화면 맨
              아래 따로 있어 같은 기기 이야기가 두 곳으로 갈렸다). 상태가 나쁘면 접힌 머리에 그 항목을 적는다.
            */}
            <Disclosure className="mt-3" title="기기 정보" summary={deviceInfoSummary(info.health)}>
              <DeviceInfo client={client} display={info} canLogs={canDelete} />
            </Disclosure>
          </div>
        </Panel>

        {/*
          편성 배정은 **고치는 카드**다. 고칠 수 없는 사람에게는 고를 수 없는 select 만
          남는데, "지금 무엇이 걸려 있나" 는 위의 "지금 화면" 과 물음표가 이미 답한다.

          **바로 보내기도 여기 있다.** 편성을 정하는 자리와 편성을 잠시 덮는 자리는 같은
          질문의 앞뒤다 — "이 화면에 무엇을 틀까". 위쪽 동작 줄에 두었을 때는 새로고침·
          변경 이력과 나란히 서서 화면을 들여다보는 도구처럼 보였다. 배정 칸 옆에 두면
          "평소 편성은 이것, 지금 잠깐은 저것" 이라는 관계가 자리에서 드러난다.
        */}
        {canEdit ? (
        <Panel
          className="xl:col-start-3 xl:row-start-2"
          title="편성 배정"
          help="바꾸면 다음 편성 조회부터 반영되며, 오프라인 화면은 다시 연결될 때 받습니다. 평소 편성을 두고 잠시 다른 콘텐츠를 틀려면 바로 보내기를 쓰세요."
          actions={
            /*
              꺼진 화면에도 걸어 둘 수 있다 — 창이 '꺼진 화면에도 걸어 두기' 를 묻고, 걸어 두면 다시 연결될
              때 나간다. 그 전까지 위쪽 띠가 '전달 대기' 로 말한다(받아 간 시각은 서버가 적는다).
            */
            <button type="button" className="btn" onClick={() => setSending(true)}>
              <Send className="h-3.5 w-3.5" strokeWidth={2} />
              바로 보내기
            </button>
          }
        >
          <div className="p-4">
            <PlaybackPicker
              label="편성 배정"
              none="없음 (그룹 상속)"
              value={pickFromAssignmentKey(assignmentValue)}
              sources={{ playlists: playlists.data?.items ?? [], schedules: schedules.data?.items ?? [] }}
              disabled={action.pending}
              onChange={(next) => changeAssignment(assignmentKeyFromPick(next))}
            />
            {override ? (
              <p
                role="status"
                className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12.5px] leading-relaxed text-amber-900"
              >
                {override.kind === 'campaign' ? (
                  <>
                    지금은 이 화면의 편성이 캠페인{overrideCampaignName ? ` 「${overrideCampaignName}」` : ''} 에 덮여 있어,
                    배정한 편성은 그 캠페인이 끝나거나 꺼질 때까지 화면에 나가지 않습니다.{' '}
                    <Link to="/campaigns" className="font-semibold underline">
                      캠페인 보기
                    </Link>
                  </>
                ) : override.kind === 'emergency' ? (
                  '지금은 긴급 송출이 발령 중이라, 배정한 편성은 해제될 때까지 화면에 나가지 않습니다.'
                ) : override.kind === 'direct' ? (
                  '지금은 바로 보내기가 걸려 있어, 배정한 편성은 위쪽 띠에서 [해제] 하거나 끝날 때까지 화면에 나가지 않습니다.'
                ) : (
                  '지금은 운영 시간 밖이라 화면이 쉬고 있습니다. 배정한 편성은 여는 시각부터 나갑니다.'
                )}
              </p>
            ) : null}
            {info.online ? null : (
              <p className="hint mt-2">화면이 꺼져 있습니다. 바로 보내기는 걸어 두었다가 다시 연결되면 나갑니다.</p>
            )}
            {storageShortfalls.length > 0 ? (
              <div className="mt-2">
                <StorageWarning shortfalls={storageShortfalls} />
              </div>
            ) : null}
          </div>
        </Panel>
        ) : null}

      </div>

      {/*
        왜 이게 나오나.

        **기준을 먼저, 이번 판정을 그 다음에.** 예전에는 trace 만 늘어놓았는데, 단계 이름
        (emergency·direct·campaign)은 그 순서를 이미 아는 사람에게만 읽혔다. 무엇이 무엇을
        덮는지를 먼저 적고, 그 아래에 이 화면의 이번 판정을 붙인다.
      */}
      {showWhy ? (
        <Modal title="편성 결정 근거" onClose={() => setShowWhy(false)}>
          <div className="flex flex-col gap-3">
            {/*
              이 화면의 판정 결과가 먼저, 모든 화면에 같은 규칙(다섯 단계 순서)은 [결정 순서] 뒤에 접는다(점진적 공개) —
              이 창을 연 사람의 질문은 "이 화면은 왜 이걸 트나" 이고, 규칙은 처음 한 번 읽으면 된다.
            */}
            <p className="hint">이 화면의 판정 결과 · ✓ 표시가 적용된 단계</p>
            {preview.loading && !preview.data ? <Loading /> : null}
            {preview.error ? <ErrorNote message={preview.error} /> : null}
            {preview.data ? (
              <>
                <ol className="trace">
                  {traceInPriorityOrder(preview.data.trace).map((entry, index) => (
                    <li key={index} className={entry.matched ? 'trace-hit' : 'trace-miss'}>
                      <span className="trace-stage">{traceStageLabel(entry.stage)}</span>
                      <span>{entry.reason}</span>
                    </li>
                  ))}
                </ol>
                {preview.data.empty ? (
                  <Empty
                    title="재생할 항목이 없습니다"
                    hint="편성을 배정하거나 플레이리스트에 콘텐츠를 추가하세요."
                  />
                ) : null}
              </>
            ) : null}
            <Disclosure title="결정 순서" summary="긴급 → 바로 보내기 → 캠페인 → 화면 배정 → 그룹 배정">
              <p className="hint">
                재생할 콘텐츠는 아래 순서로 정해지며, <strong>위 단계가 아래 단계보다 우선합니다.</strong>
              </p>
              <ol className="why-steps">
                <li>
                  <strong>긴급 오버라이드</strong> — 발령 중이면 모든 편성을 대체합니다.
                </li>
                <li>
                  <strong>바로 보내기</strong> — 이 화면에 임시로 보낸 콘텐츠. 만료되면 평소 편성으로 돌아갑니다.
                </li>
                <li>
                  <strong>캠페인</strong> — 게재 중이면 아래 배정을 대체하거나(전체 대체) 사이사이에 끼웁니다(삽입).
                </li>
                <li>
                  <strong>화면 배정</strong> — 이 화면에 배정한 플레이리스트·스케줄. 스케줄은 그 시각의 규칙을 따릅니다.
                </li>
                <li>
                  <strong>그룹 배정</strong> — 화면 배정이 없으면 속한 그룹의 배정을 따릅니다.
                </li>
              </ol>
            </Disclosure>
          </div>
        </Modal>
      ) : null}

      {showSettings ? (
        <Modal title="화면 설정" onClose={() => setShowSettings(false)}>
          <DisplaySettings
            display={info}
            versions={versions}
            capabilities={me.capabilities}
            pending={action.pending}
            onPatch={(patch) =>
              void action.run(
                () => client.updateDisplay(id, patch),
                () => {
                  display.reload();
                  audit.reload();
                },
              )
            }
            onAgentSettings={(change) =>
              void action.run(
                () => client.setAgentSettings([id], change),
                () => {
                  display.reload();
                  audit.reload();
                },
              )
            }
            onCommand={(kind) =>
              void action.run(
                () => client.sendDisplayCommand(id, kind),
                () => {
                  display.reload();
                  audit.reload();
                },
              )
            }
            onPin={(pin) =>
              void action.run(
                () => client.setMaintenancePin([id], pin),
                () => {
                  display.reload();
                  audit.reload();
                },
              )
            }
          />
        </Modal>
      ) : null}

      {/*
        변경 이력은 **찾을 때만 본다.**

        늘 펼쳐 두면 화면 한 자리를 계속 차지하는데, 정작 볼 일은 "누가 이걸 바꿨지"가
        떠오른 순간뿐이다. 그때는 10건이 아니라 쭉 훑고 싶으므로 모달에서 50건을 준다.
      */}
      {showLog ? (
        <Modal title="변경 이력" onClose={() => setShowLog(false)}>
          <div className="sm:p-4">
            <p className="hint mb-3">
              최근 변경 50건
              <HelpTip
                text="화면이 스스로 보고하는 상태(재생 중인 콘텐츠·하트비트)는 변경 이력에 남지 않습니다."
                label="변경 이력 도움말"
              />
            </p>
            {audit.loading && !audit.data ? <Loading /> : null}
            {audit.error ? <ErrorNote message={audit.error} /> : null}
            {!audit.loading && (audit.data?.items.length ?? 0) === 0 ? (
              <p className="hint">기록된 변경이 없습니다.</p>
            ) : (
              <ul className="log-list">
                {audit.data?.items.map((entry) => {
                  const actor = formatActor(entry.actor);
                  const changes = describeAudit(entry, lookupName);
                  const exactAt = new Date(entry.created_at).toLocaleString('ko-KR');
                  return (
                    <li key={entry.id}>
                      <div className="log-head">
                        <span className="log-action">{ACTION_LABEL[entry.action] ?? entry.action}</span>
                        <span className="log-actor">
                          {actor.name}
                          {actor.kind ? <span className="hint hint-inline">{actor.kind}</span> : null}
                        </span>
                        {/*
                          상대 시각으로 훑고, 정확한 시각은 짚었을 때 준다 — 사고 조사는 분 단위로 맞춰야 한다.
                          짚을 수 없는 터치 화면에서는 정확한 시각을 옆에 적어 둔다.
                        */}
                        <span className="log-when" title={exactAt}>
                          <RelTime iso={entry.created_at} />
                          <span className="hidden [@media(hover:none)]:inline"> · {exactAt}</span>
                        </span>
                      </div>
                      {changes.length > 0 ? (
                        <ul className="log-changes">
                          {changes.map((line, index) => (
                            <li key={`${entry.id}-${index}`}>{line}</li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Modal>
      ) : null}

      {sending ? (
        <DirectSendModal
          client={client}
          targets={info.online ? [{ id, name: info.name }] : []}
          skipped={info.online ? [] : [{ id, name: info.name }]}
          onClose={() => setSending(false)}
          onSent={() => {
            setSending(false);
            display.reload();
            preview.reload();
            audit.reload();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * 온 곳으로 돌아간다 — **뒤로가기**다.
 *
 * 늘 화면 목록으로 보냈더니, 모니터링에서 카드를 눌러 들어온 사람이 벽으로 못 돌아갔다.
 * 벽은 지점·크기·자리를 꾸며 놓고 보는 화면이라, 목록으로 떨어지면 다시 벽을 찾아가야 한다.
 *
 * 어디서 왔는지는 **보내는 쪽이 말해 준다**(`state.from`). 브라우저 기록만 보고 판단하면
 * 새 탭으로 연 상세·북마크로 들어온 상세에서 엉뚱한 곳(직전에 보던 남의 사이트)으로 간다.
 * 말해 주지 않았으면 화면 목록이다 — 상세의 제자리이자, 어디서 왔든 말이 되는 곳이다.
 */
export const BACK_LABEL: Readonly<Record<string, string>> = {
  '/wall': '모니터링으로',
  '/': '대시보드로',
};

function BackLink(): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const from = typeof (location.state as { from?: unknown } | null)?.from === 'string'
    ? ((location.state as { from: string }).from)
    : null;

  return (
    <button
      type="button"
      className="flex w-fit items-center gap-1.5 text-[13.5px] text-slate-500 hover:text-slate-800"
      /* 온 곳을 알면 진짜 뒤로 간다 — 그 화면이 보던 자리(스크롤·필터)로 돌아간다. */
      onClick={() => (from ? navigate(-1) : navigate('/displays'))}
    >
      <ArrowLeft className="h-4 w-4" strokeWidth={2} />
      {(from && BACK_LABEL[from]) ?? '목록으로'}
    </button>
  );
}
